import * as React from 'react';
import {
  styled, Theme, CSSObject,
} from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PropTypes, { InferProps } from 'prop-types';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(
  MuiDrawer, { shouldForwardProp: (property) => property !== 'open' },
)(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const DrawerWrapper = styled(Box)<{
  open: boolean;
}>`
  display: flex;
  span {
    display: inline-flex;
    visibility: ${({ open }) => (
    open ? 'visible' : 'hidden'
  )};
  }
`;

const dashboardDrawerPropertyTypes = {
  children: PropTypes.node,
  open: PropTypes.bool.isRequired,
  onToggleDrawer: PropTypes.func.isRequired,
};

type DashboardDrawerProperties = InferProps<
  typeof dashboardDrawerPropertyTypes
>;

const DashboardDrawer = (
  { children, open, onToggleDrawer }: DashboardDrawerProperties,
) => (
  <DrawerWrapper open={open}>
    <Drawer variant="permanent" open={open} role="menu">
      <DrawerHeader>
        <IconButton onClick={onToggleDrawer}>
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />
      {children}
    </Drawer>
  </DrawerWrapper>
);

export default DashboardDrawer;
